import { Template } from './fill'

/**
 * Default templates (POS.md §18.9 §4). Data is pre-formatted by the app (money as text), so templates only place
 * values. Business options (logo, header / footer text, which fields show) are data fields the templates test with
 * `if`; the visual editor comes later and edits this same JSON.
 */
export const DEFAULT_TEMPLATES: Record<TemplateKey, Template> = {
  receipt: {
    kind: 'roll',
    cut: true,
    blocks: [
      { type: 'image', src: '{{shop.logo}}', width: 30, if: 'options.show_logo' },
      { type: 'text', text: '{{shop.name}}', align: 'center', bold: true, size: 2 },
      { type: 'text', text: '{{shop.address}}', align: 'center', if: 'shop.address' },
      { type: 'text', text: '{{shop.phone}}', align: 'center', if: 'shop.phone' },
      { type: 'text', text: '{{shop.email}}', align: 'center', if: 'shop.email' },
      { type: 'text', text: 'Reg. {{shop.registration_no}}', align: 'center', if: 'shop.registration_no' },
      { type: 'text', text: 'BIN {{shop.tax_id}}', align: 'center', if: 'shop.tax_id' },
      { type: 'text', text: '{{options.header}}', align: 'center', if: 'options.header' },
      { type: 'rule' },
      { type: 'row', left: '{{order.number}}', right: '{{order.date}}' },
      { type: 'text', text: '{{order.counter}} - {{order.cashier}}', if: 'order.counter' },
      { type: 'row', left: '{{order.customer.name}}', right: '{{order.customer.phone}}', if: 'order.customer' },
      { type: 'rule' },
      {
        type: 'repeat',
        each: 'order.lines',
        blocks: [
          { type: 'text', text: '{{it.name}}' },
          { type: 'row', left: '  {{it.qty}} x {{it.price}}{{it.note}}', right: '{{it.total}}' },
        ],
      },
      { type: 'rule' },
      { type: 'row', left: 'Subtotal', right: '{{order.subtotal}}' },
      { type: 'row', left: 'Discount', right: '-{{order.discount}}', if: 'order.discount' },
      { type: 'row', left: 'VAT', right: '{{order.tax}}', if: 'order.tax' },
      { type: 'row', left: 'Adjustment', right: '-{{order.adjustment}}', if: 'order.adjustment' },
      { type: 'row', left: 'TOTAL', right: '{{order.total}}', bold: true },
      { type: 'rule' },
      { type: 'repeat', each: 'order.payments', blocks: [{ type: 'row', left: '{{it.method}}', right: '{{it.amount}}' }] },
      { type: 'row', left: 'Change', right: '{{order.change}}', bold: true, if: 'order.change' },
      { type: 'rule' },
      { type: 'text', text: 'Items: {{order.items}} - {{options.footer}}', align: 'center' },
      { type: 'text', text: 'Exchange within {{shop.return_days}} days with receipt.', align: 'center', if: 'shop.return_days' },
      { type: 'barcode', value: '{{order.number}}', symbology: 'code128', height: 10 },
    ],
  },
  slip: {
    kind: 'roll',
    cut: true,
    blocks: [
      { type: 'text', text: '{{shop.name}}', align: 'center', bold: true },
      { type: 'text', text: '{{shop.address}}', align: 'center', if: 'shop.address' },
      { type: 'rule' },
      { type: 'text', text: '{{slip.title}}', align: 'center', bold: true },
      { type: 'row', left: '{{slip.ref}}', right: '{{slip.date}}' },
      { type: 'rule' },
      { type: 'repeat', each: 'slip.rows', blocks: [{ type: 'row', left: '{{it.label}}', right: '{{it.value}}' }] },
      { type: 'rule' },
      { type: 'barcode', value: '{{slip.ref}}', symbology: 'code128', height: 10 },
    ],
  },
  z_report: {
    kind: 'roll',
    cut: true,
    blocks: [
      { type: 'text', text: '{{shop.name}}', align: 'center', bold: true },
      { type: 'text', text: '{{shift.title}}', align: 'center', bold: true },
      { type: 'row', left: '{{shift.counter}}', right: '{{shift.cashier}}' },
      { type: 'row', left: 'Opened', right: '{{shift.opened}}' },
      { type: 'row', left: 'Closed', right: '{{shift.closed}}' },
      { type: 'rule' },
      { type: 'repeat', each: 'shift.rows', blocks: [{ type: 'row', left: '{{it.label}}', right: '{{it.value}}' }] },
      { type: 'rule' },
    ],
  },
  product_label: {
    kind: 'label',
    blocks: [
      { type: 'text', text: '{{product.name}}', bold: true },
      { type: 'text', text: '{{product.price}}', size: 2, if: 'options.show_price' },
      { type: 'barcode', value: '{{product.barcode}}', symbology: 'code128', height: 9 },
    ],
  },
  shelf_label: {
    kind: 'label',
    blocks: [
      { type: 'text', text: '{{product.name}}', bold: true },
      { type: 'row', left: 'per {{product.unit}}', right: '{{product.price}}', bold: true },
      { type: 'text', text: 'Was {{product.compare_price}}', if: 'product.compare_price' },
      { type: 'barcode', value: '{{product.barcode}}', symbology: 'code128', height: 7 },
    ],
  },
  customer_card: {
    kind: 'label',
    blocks: [
      { type: 'text', text: '{{shop.name}}', align: 'center', bold: true },
      { type: 'text', text: '{{customer.name}}', align: 'center' },
      { type: 'barcode', value: '{{customer.barcode}}', symbology: 'code128', height: 10 },
    ],
  },
  staff_card: {
    kind: 'label',
    blocks: [
      { type: 'text', text: '{{shop.name}}', align: 'center', bold: true },
      { type: 'text', text: '{{staff.name}}', align: 'center', size: 2 },
      { type: 'barcode', value: '{{staff.barcode}}', symbology: 'code128', height: 10 },
    ],
  },
}

export type TemplateKey = 'receipt' | 'slip' | 'z_report' | 'product_label' | 'shelf_label' | 'customer_card' | 'staff_card'

/** Which role prints each template by default. */
export const TEMPLATE_ROLE: Record<TemplateKey, 'receipt' | 'label'> = {
  receipt: 'receipt',
  slip: 'receipt',
  z_report: 'receipt',
  product_label: 'label',
  shelf_label: 'label',
  customer_card: 'label',
  staff_card: 'label',
}

/** A4 sheet layouts for printing labels and cards on the `document` role (regular printer). */
export const SHEET_LAYOUTS = {
  labels_3x8: { columns: 3, rows: 8, cell_width_mm: 63.5, cell_height_mm: 33.9, margin_mm: 10, gap_mm: 2.5 },
  labels_4x10: { columns: 4, rows: 10, cell_width_mm: 48.5, cell_height_mm: 25.4, margin_mm: 8, gap_mm: 0 },
  cards_2x5: { columns: 2, rows: 5, cell_width_mm: 85.6, cell_height_mm: 54, margin_mm: 12, gap_mm: 4 },
} as const
